import { FormProvider } from "react-hook-form";
import { nameof } from "ts-simple-nameof";
import styles from "./PersonalDataSection.module.scss";
import { IUserModel } from "../../../../common/infrastructure/hooks/useAuthentication";
import { useMyProfileForm } from "../hooks/useMyProfileForm";
import { IMyProfileFormModel } from "../models/myProfileFormModel";
import { DefaultInputField } from "../../../../common/components/forms/inputs/InputField/DefaultInputField";
import { DefaultPhoneInputField } from "../../../../common/components/forms/inputs/phoneField/DefaultPhoneInputField";
import { BirthDateField } from "../../../../common/components/forms/inputs/birthDateField/BirthDateField";
import { DefaultSliderField } from "../../../../common/components/forms/inputs/sliderField/DefaultSliderField";
import { DefaultButton } from "../../../../common/components/button/defaultButton/DefaultButton";
import { dateUtility } from "../../../../common/infrastructure/utilities/dateUtility";
import { Icon, IconType } from "../../../../common/icons/Icon";

interface IPersonalDataSectionProps {
  profile: IUserModel | undefined;
  isEditing: boolean;
  onEditToggle: () => void;
  onSaveSuccess: () => void;
}

export const PersonalDataSection = ({
  profile,
  isEditing,
  onEditToggle,
  onSaveSuccess,
}: IPersonalDataSectionProps) => {
  const { form, onSubmit, isSaving, isSignInLocked } = useMyProfileForm({
    profile,
    onSuccess: () => {
      onSaveSuccess();
      onEditToggle();
    },
  });

  const viewMode = (
    <div className={styles.viewMode}>
      <div className={styles.field}>
        <span className={styles.label}>Име</span>
        <span className={styles.value}>
          {profile?.firstName} {profile?.lastName}
        </span>
      </div>
      <div className={styles.field}>
        <span className={styles.label}>Телефон</span>
        <span className={styles.value}>{profile?.phoneNumber || "—"}</span>
      </div>
      <div className={styles.field}>
        <span className={styles.label}>Имейл</span>
        <span className={styles.value}>{profile?.email || "—"}</span>
      </div>
      <div className={styles.field}>
        <span className={styles.label}>Рожден ден</span>
        <span className={styles.value}>
          {profile?.birthDate ? dateUtility.formatDate(profile.birthDate) : "—"}
        </span>
      </div>
      <div className={styles.field}>
        <span className={styles.label}>Размер на шрифта</span>
        <span className={styles.value}>{profile?.fontSizePercent ?? 100}%</span>
      </div>
      <div className={styles.editRow}>
        <button type="button" className={styles.editBtn} onClick={onEditToggle}>
          <Icon
            type={IconType.EditIcon}
            width="14px"
            height="14px"
            color="currentColor"
          />
          Редактирай
        </button>
      </div>
    </div>
  );

  const editMode = (
    <FormProvider {...form}>
      <form onSubmit={onSubmit} className={styles.editMode} noValidate>
        <DefaultInputField
          name={nameof<IMyProfileFormModel>((x) => x.firstName)}
          label="Име"
          placeholder="Иван"
          required
          width="100%"
        />
        <DefaultInputField
          name={nameof<IMyProfileFormModel>((x) => x.lastName)}
          label="Фамилия"
          placeholder="Иванов"
          width="100%"
        />
        <DefaultPhoneInputField
          name={nameof<IMyProfileFormModel>((x) => x.phoneNumber)}
          label="Телефон"
          width="100%"
          disabled={isSignInLocked}
        />
        <DefaultInputField
          name={nameof<IMyProfileFormModel>((x) => x.email)}
          label="Имейл"
          placeholder="email@example.com"
          width="100%"
          disabled={isSignInLocked}
        />
        {isSignInLocked && (
          <p className={styles.lockedHint}>
            Телефонът и имейлът не се сменят в профил, отворен от админ панела.
          </p>
        )}
        <BirthDateField
          name={nameof<IMyProfileFormModel>((x) => x.birthDate)}
          label="Рожден ден"
          width="100%"
        />
        <DefaultSliderField
          name={nameof<IMyProfileFormModel>((x) => x.fontSizePercent)}
          label="Размер на шрифта"
          min={80}
          max={140}
          step={5}
          valueSuffix="%"
          width="100%"
          tooltip="Променете размера на шрифта в приложението. 100% е стандартният размер."
        />
        <div className={styles.formActions}>
          <DefaultButton
            label="Отказ"
            type="button"
            design="outline"
            onClick={onEditToggle}
            disabled={isSaving}
          />
          <DefaultButton
            label="Запази"
            type="submit"
            design="accent"
            loading={isSaving}
          />
        </div>
      </form>
    </FormProvider>
  );

  return (
    <section className={styles.section}>
      {isEditing ? editMode : viewMode}
    </section>
  );
};
